import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  query,
  where,
} from 'firebase/firestore';
import type { Occupation, OccupationCategory } from '../../types';
import { db } from '../../lib/firebase';
import { categories, occupations } from '../mock/data';
import { delay } from '../mock/delay';
import { tokenService } from '../membership/tokenService'; // ADDED FOR TOKEN SYSTEM

// =============================================================================
// Occupation data — reads from Firestore (`careers` + `careerCategories`,
// populated by firebase-functions/scripts/migrate-careers-to-firestore.js).
// Falls back to the local mock dataset whenever Firestore is empty or
// unreachable, so the app stays fully runnable without a migrated backend.
// The SOC code on each doc is what business AI automation matches on.
// =============================================================================

const CAREERS = 'careers';
const CATEGORIES = 'careerCategories';

// Probe once per session whether the Firestore `careers` collection has data.
let firestoreModeProbe: Promise<boolean> | null = null;
async function useFirestore(): Promise<boolean> {
  if (!firestoreModeProbe) {
    firestoreModeProbe = (async () => {
      try {
        const snap = await getDocs(query(collection(db, CAREERS), fbLimit(1)));
        return !snap.empty;
      } catch {
        return false; // offline / rules / not migrated → use mock
      }
    })();
  }
  return firestoreModeProbe;
}

type CareerDoc = Record<string, unknown>;

function mapCareer(id: string, d: CareerDoc): Occupation {
  const str = (k: string, dflt = '') => (typeof d[k] === 'string' ? (d[k] as string) : dflt);
  const num = (k: string, dflt = 0) => (typeof d[k] === 'number' ? (d[k] as number) : dflt);
  const arr = (k: string) => (Array.isArray(d[k]) ? (d[k] as string[]) : []);
  const demand = str('demand', 'medium') as Occupation['demand'];
  return {
    id: str('id') || id,
    title: str('name') || str('title') || 'Untitled occupation',
    category: str('category') || 'General',
    categoryId: str('categoryId') || 'general',
    description: str('description'),
    salary: str('salary', 'N/A'),
    salaryMin: num('salaryMin'),
    salaryMax: num('salaryMax'),
    growth: str('growth', `${num('growthRate')}%`),
    growthRate: num('growthRate'),
    skills: arr('skills').length ? arr('skills') : arr('tags'),
    demand: (['low', 'medium', 'high', 'very-high'] as const).includes(demand) ? demand : 'medium',
    education: str('education'),
    openings: num('openings'),
    trending: d.trending === true,
    recommended: d.recommended === true,
    icon: str('icon') || undefined,
  };
}

function mapCategory(id: string, d: CareerDoc): OccupationCategory {
  const str = (k: string, dflt = '') => (typeof d[k] === 'string' ? (d[k] as string) : dflt);
  const num = (k: string, dflt = 0) => (typeof d[k] === 'number' ? (d[k] as number) : dflt);
  return {
    id: str('id') || id,
    name: str('name') || 'Category',
    description: str('description'),
    occupationCount: num('occupationCount'),
    icon: str('icon', 'Briefcase'),
    color: str('color', 'blue'),
  };
}

export const occupationService = {
  async getOccupations(): Promise<Occupation[]> {
    if (await useFirestore()) {
      try {
        const snap = await getDocs(query(collection(db, CAREERS), where('isPublic', '==', true), fbLimit(60)));
        if (!snap.empty) return snap.docs.map((s) => mapCareer(s.id, s.data()));
      } catch { /* fall through to mock */ }
    }
    return delay(occupations);
  },

  async getOccupationById(id: string): Promise<Occupation | undefined> {
    if (await useFirestore()) {
      try {
        const snap = await getDoc(doc(db, CAREERS, id));
        if (snap.exists()) return mapCareer(snap.id, snap.data());
      } catch { /* fall through */ }
    }
    return delay(occupations.find((o) => o.id === id));
  },

  async getCategories(): Promise<OccupationCategory[]> {
    if (await useFirestore()) {
      try {
        const snap = await getDocs(collection(db, CATEGORIES));
        if (!snap.empty) {
          return snap.docs
            .map((s) => mapCategory(s.id, s.data()))
            .sort((a, b) => a.name.localeCompare(b.name));
        }
      } catch { /* fall through */ }
    }
    return delay(categories);
  },

  async getCategoryById(id: string): Promise<OccupationCategory | undefined> {
    if (await useFirestore()) {
      try {
        const snap = await getDoc(doc(db, CATEGORIES, id));
        if (snap.exists()) return mapCategory(snap.id, snap.data());
      } catch { /* fall through */ }
    }
    return delay(categories.find((c) => c.id === id));
  },

  // "See everything in a category" — query all public occupations in the group.
  async getByCategory(categoryId: string): Promise<Occupation[]> {
    if (await useFirestore()) {
      try {
        const snap = await getDocs(
          query(collection(db, CAREERS), where('categoryId', '==', categoryId), fbLimit(500)),
        );
        if (!snap.empty) {
          return snap.docs
            .map((s) => mapCareer(s.id, s.data()))
            .filter((o) => o.title && o.title !== 'Untitled occupation')
            .sort((a, b) => a.title.localeCompare(b.title));
        }
      } catch { /* fall through */ }
    }
    return delay(occupations.filter((o) => o.categoryId === categoryId));
  },

  async getTrending(): Promise<Occupation[]> {
    if (await useFirestore()) {
      try {
        // Real trending signal — set by scripts/update-trending-careers.js.
        const snap = await getDocs(query(collection(db, CAREERS), where('trending', '==', true), fbLimit(24)));
        if (!snap.empty) {
          return snap.docs
            .map((s) => mapCareer(s.id, s.data()))
            .sort((a, b) => b.growthRate - a.growthRate);
        }
      } catch { /* fall through */ }
    }
    return delay(occupations.filter((o) => o.trending));
  },

  async getRecommended(): Promise<Occupation[]> {
    if (await useFirestore()) {
      try {
        const snap = await getDocs(query(collection(db, CAREERS), where('isPublic', '==', true), fbLimit(8)));
        if (!snap.empty) return snap.docs.map((s) => ({ ...mapCareer(s.id, s.data()), recommended: true }));
      } catch { /* fall through */ }
    }
    return delay(occupations.filter((o) => o.recommended));
  },

  // ADDED FOR TOKEN SYSTEM: gated "generate AI recommendations" action. Charges
  // atomically (throws InsufficientTokensError if the balance is too low).
  async generateRecommendations(): Promise<Occupation[]> {
    await tokenService.charge('ai_recommendations', 'AI occupation recommendations');
    const recommended = await this.getRecommended();
    // Shuffle a little so a re-run visibly "regenerates".
    return [...recommended].sort(() => Math.random() - 0.5);
  },

  async search(query_: string): Promise<Occupation[]> {
    const q = query_.trim().toLowerCase();
    if (await useFirestore()) {
      try {
        // Firestore has no full-text search; pull a capped public set and filter.
        const snap = await getDocs(query(collection(db, CAREERS), where('isPublic', '==', true), fbLimit(300)));
        if (!snap.empty) {
          const all = snap.docs.map((s) => mapCareer(s.id, s.data()));
          if (!q) return all.slice(0, 60);
          return all.filter(
            (o) =>
              o.title.toLowerCase().includes(q) ||
              o.category.toLowerCase().includes(q) ||
              o.skills.some((s) => s.toLowerCase().includes(q)),
          );
        }
      } catch { /* fall through */ }
    }
    if (!q) return delay(occupations);
    return delay(
      occupations.filter(
        (o) =>
          o.title.toLowerCase().includes(q) ||
          o.category.toLowerCase().includes(q) ||
          o.skills.some((s) => s.toLowerCase().includes(q)),
      ),
    );
  },
};
